import { Link } from 'react-router-dom'
import { ChevronRight, Users, Clock, Utensils } from 'lucide-react'
import { useI18n } from '../../i18n/useI18n'
import { useCoursesByStore } from '../../hooks/useCourses'

/**
 * #737: StoreDetail のタブとして表示されるコース一覧。
 *
 * 食べログ準拠で「コース」カード形式でリスト表示。tap で /stores/:id/courses/:courseId へ。
 * アクティブコースが 0 件のときも tab body は描画される (空状態メッセージ表示) — タブ自体の
 * 表示/非表示は呼び出し元 (StoreDetail) で reservationEnabled を見て制御する想定。
 */
export default function CoursesSection({ storeId }: { storeId: string }) {
  const { t } = useI18n()
  const { courses, loading } = useCoursesByStore(storeId)

  if (loading) {
    return (
      <div className="px-4 py-8 text-center text-text-tertiary font-body text-sm">
        {t('common.loading')}
      </div>
    )
  }

  if (courses.length === 0) {
    return (
      <div className="px-4 py-12 text-center">
        <Utensils size={32} className="mx-auto text-text-tertiary mb-2" />
        <p className="font-body text-sm text-text-secondary">{t('coursesSection.empty')}</p>
      </div>
    )
  }

  return (
    <div className="px-4 py-3 space-y-3">
      {courses.map((course) => (
        <Link
          key={course.id}
          to={`/stores/${storeId}/courses/${course.id}`}
          className="block bg-white border border-gray-100 rounded-xl overflow-hidden tap-card"
        >
          <div className="flex gap-3 p-3">
            {/* `photoUrls` を持たないコース doc (API 経由以外で作られた古いデータ等) で
                `undefined[0]` になり、**店舗ページ全体が error boundary で落ちていた**。
                コース 1 件のデータ欠けで店の全メニューが見えなくなるのは割に合わない。 */}
            {course.photoUrls?.[0] && (
              <img
                src={course.photoUrls[0]}
                alt=""
                className="w-24 h-24 object-cover rounded-lg flex-shrink-0 bg-gray-100"
                loading="lazy"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-body text-sm font-semibold text-text-primary line-clamp-2 leading-snug">
                {course.name}
              </h3>
              <p className="font-display text-base font-bold text-yokoso-red mt-1">
                ¥{course.priceYen.toLocaleString()}
                <span className="text-xs font-body text-text-secondary ml-1">
                  / {t('coursesSection.perPerson')}
                </span>
              </p>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-text-tertiary font-body">
                <span className="flex items-center gap-1">
                  <Users size={12} />
                  {t('coursesSection.partyRange', {
                    min: String(course.partySizeRange.min),
                    max: String(course.partySizeRange.max),
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {course.durationMinutes}
                  {t('coursesSection.durationUnit')}
                </span>
              </div>
            </div>
            <ChevronRight size={16} className="text-text-tertiary self-center flex-shrink-0" />
          </div>
        </Link>
      ))}
    </div>
  )
}
